/**
 * web/src/hooks/mutations/useDeleteUser.ts
 *
 * DELETE /api/admin/users/:id — admin-only hard delete.
 *
 * Optimistically removes the user from the qk.adminAccess() cache so the
 * matrix row disappears immediately. Rolls back on error with a toast.
 * Also drops every grant referencing that user (the server cascades the
 * DB row; we mirror that here so the cell counts stay honest until the
 * refetch lands).
 */
import { deleteAdminUser } from '../../api/access';
import { useOptimisticMatrixMutation } from './_optimistic';

/**
 * React Query mutation hook for hard-deleting a user. Uses the shared
 * admin-matrix optimistic wrapper (F4): drops the user row and every grant
 * referencing it immediately (mirroring the server cascade), rolls back on
 * error.
 *
 * @returns React Query mutation handle keyed off the target user id
 * @sideEffects HTTP DELETE; optimistic cache mutation; toast on outcome
 * @idempotent no — re-deleting after success throws 404
 */
export function useDeleteUser() {
  return useOptimisticMatrixMutation<string>(
    (userId) => deleteAdminUser(userId),
    (prev, userId) => ({
      ...prev,
      users: prev.users.filter((u) => u.id !== userId),
      eventGrants: prev.eventGrants.filter((g) => g.userId !== userId),
    }),
    { errorFallback: 'Failed to delete user', successMessage: 'User deleted' },
  );
}
